import React, { useState, useEffect, useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring, useInView, AnimatePresence } from "framer-motion";
import { Phone, ChevronDown, Sparkles, Target, Leaf, Shield, Lock, Unlock, Star, Zap } from "lucide-react";
import Footer from "../../Components/Footer";
import { useLanguage } from "../../contexts/LanguageContext";

export default function AboutSection({ onNavigate, onOpenPrivacyPolicy, onOpenCookiePolicy, onOpenTermsOfService }) {
  const { t } = useLanguage();
  const [expandedValue, setExpandedValue] = useState(null);
  const [count, setCount] = useState(0);
  const [counterPhase, setCounterPhase] = useState("fullscreen"); // "fullscreen" | "transitioning" | "header"
  const [hasStarted, setHasStarted] = useState(false);

  // Animated counter for "40 anni" - starts automatically
  useEffect(() => {
    if (!hasStarted) {
      setHasStarted(true);

      // Small delay to ensure component is mounted
      setTimeout(() => {
        let start = 0;
        const end = 40;
        const duration = 2500;
        const increment = end / (duration / 16);

        const timer = setInterval(() => {
          start += increment;
          if (start >= end) {
            setCount(end);
            clearInterval(timer);
            // Wait 1 second after counter finishes, then transition
            setTimeout(() => {
              setCounterPhase("transitioning");
              setTimeout(() => setCounterPhase("header"), 800);
            }, 1000);
          } else {
            setCount(Math.floor(start));
          }
        }, 16);

        return () => clearInterval(timer);
      }, 300);
    }
  }, [hasStarted]);

  const containerAnimation = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2,
      },
    },
  };

  const itemAnimation = {
    hidden: { opacity: 0, y: 40, scale: 0.9 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 15,
      },
    },
  };

  const valuesData = [
    {
      id: "innovazione",
      title: t.about.valInnovazioneTitle,
      icon: Sparkles,
      short: t.about.valInnovazioneShort,
      full: t.about.valInnovazioneFull
    },
    {
      id: "esperienza",
      title: t.about.valEsperienzaTitle,
      icon: Target,
      short: t.about.valEsperienzaShort,
      full: t.about.valEsperienzaFull
    },
    {
      id: "sostenibilita",
      title: t.about.valSostenibilitaTitle,
      icon: Leaf,
      short: t.about.valSostenibilitaShort,
      full: t.about.valSostenibilitaFull
    },
    {
      id: "affidabilita",
      title: t.about.valAffidabilitaTitle,
      icon: Shield,
      short: t.about.valAffidabilitaShort,
      full: t.about.valAffidabilitaFull
    }
  ];

  return (
    <>
      {/* Full-Screen Counter Overlay */}
      <AnimatePresence>
        {counterPhase === "fullscreen" && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-[--bg-secondary]"
          >
            {/* Animated background particles */}
            <div className="absolute inset-0 overflow-hidden">
              {[...Array(20)].map((_, i) => (
                <motion.div
                  key={i}
                  className="absolute w-2 h-2 bg-[--accent] rounded-full"
                  style={{
                    left: `${Math.random() * 100}%`,
                    top: `${Math.random() * 100}%`,
                  }}
                  animate={{
                    scale: [0, 1, 0],
                    opacity: [0, 0.6, 0],
                  }}
                  transition={{
                    duration: 3,
                    repeat: Infinity,
                    delay: Math.random() * 2,
                  }}
                />
              ))}
            </div>

            {/* Giant Counter */}
            <motion.div
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ type: "spring", stiffness: 150, damping: 20, delay: 0.2 }}
              className="relative"
            >
              <div className="text-[15rem] md:text-[20rem] font-display font-bold bg-gradient-to-r from-[--accent] via-yellow-400 to-[--accent] bg-clip-text text-transparent leading-none">
                {count}
              </div>

              {/* Glow effect */}
              <div className="absolute inset-0 blur-3xl bg-gradient-to-r from-[--accent]/30 via-yellow-400/30 to-[--accent]/30 -z-10"></div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <section className="min-h-screen py-24 md:py-32 bg-[--bg-secondary] relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 left-10 w-72 h-72 bg-[--accent] rounded-full opacity-5 blur-3xl" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-purple-500 rounded-full opacity-5 blur-3xl" />
        </div>

        <motion.div
          variants={containerAnimation}
          initial="hidden"
          animate="visible"
          className="max-w-7xl mx-auto px-6 relative z-10"
        >
          {/* Header with Counter */}
          <AnimatePresence>
            {counterPhase === "header" && (
              <motion.div
                initial={{ opacity: 0, y: -50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, type: "spring" }}
                className="text-center mb-24"
              >
                <motion.div className="inline-flex items-center justify-center mb-6">
                  <div className="w-12 h-px bg-gradient-to-r from-transparent via-[--accent] to-transparent" />
                  <span className="text-[--accent] text-sm font-medium tracking-[0.3em] uppercase mx-6">
                    {t.about.title}
                  </span>
                  <div className="w-12 h-px bg-gradient-to-r from-transparent via-[--accent] to-transparent" />
                </motion.div>

                <h2 className="text-4xl md:text-6xl font-display font-bold text-[--text-primary] mb-6 leading-tight">
                  {t.about.title}
                </h2>

                {/* Compact Counter in Header */}
                <div className="relative inline-block mb-8">
                  <motion.div
                    initial={{ scale: 0.8 }}
                    animate={{ scale: 1 }}
                    className="relative"
                  >
                    <div className="text-7xl md:text-8xl font-display font-bold bg-gradient-to-r from-[--accent] via-yellow-400 to-[--accent] bg-clip-text text-transparent">
                      {count}
                    </div>
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.3 }}
                      className="text-xl md:text-2xl text-[--text-secondary] mt-2"
                    >
                      {t.about.yearsOfLight}
                    </motion.div>
                  </motion.div>
                </div>

                {/* Tagline */}
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.5 }}
                  className="text-xl md:text-2xl text-[--text-secondary] italic"
                >
                  <span className="text-[--accent] font-semibold">{t.about.tagline}</span>
                </motion.p>

                {/* Scroll Indicator */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.7 }}
                  className="mt-12"
                >
                  <motion.div
                    animate={{ y: [0, 10, 0] }}
                    transition={{ repeat: Infinity, duration: 1.5 }}
                    className="inline-block"
                  >
                    <ChevronDown className="w-8 h-8 text-[--accent]" />
                  </motion.div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Hero Introduction */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6 }}
            className="mb-20"
          >
            <div className="relative bg-gradient-to-br from-[--bg-primary]/60 to-[--bg-primary]/30 backdrop-blur-sm rounded-3xl p-10 md:p-14 overflow-hidden">
              {/* Decorative elements */}
              <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-bl from-[--accent]/10 to-transparent rounded-full blur-3xl"></div>
              <div className="absolute bottom-0 left-0 w-48 h-48 bg-gradient-to-tr from-yellow-400/10 to-transparent rounded-full blur-3xl"></div>

              <div className="relative z-10">
                <div className="flex items-center justify-center mb-8">
                  <div className="h-1 w-16 bg-gradient-to-r from-[--accent] to-yellow-400 rounded-full"></div>
                </div>

                <p className="text-xl md:text-2xl text-[--text-primary] leading-relaxed text-center font-light mb-6">
                  <span className="text-[--accent] font-bold" style={{ fontFamily: "'Bauhaus 93', sans-serif", fontSize: '1.4em' }}>ELLEGI</span> {t.about.intro} <span className="text-[--accent] font-semibold">40</span> {t.about.intro2}
                </p>
              </div>
            </div>
          </motion.div>

          {/* Two Column Layout */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6 }}
            className="mb-20"
          >
            <div className="grid md:grid-cols-2 gap-8">
              {/* Left Column - Partnership */}
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="bg-[--bg-primary]/40 backdrop-blur-sm rounded-2xl p-8 md:p-10"
              >
                <div className="flex items-center mb-6">
                  <div className="w-1 h-12 bg-gradient-to-b from-[--accent] to-yellow-400 rounded-full mr-4"></div>
                  <h3 className="text-2xl font-display font-bold text-[--text-primary]">
                    {t.about.partnership}
                  </h3>
                </div>
                <p className="text-base md:text-lg text-[--text-secondary] leading-relaxed">
                  {t.about.partnershipDesc}
                </p>
              </motion.div>

              {/* Right Column - Solutions */}
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="bg-[--bg-primary]/40 backdrop-blur-sm rounded-2xl p-8 md:p-10"
              >
                <div className="flex items-center mb-6">
                  <div className="w-1 h-12 bg-gradient-to-b from-yellow-400 to-[--accent] rounded-full mr-4"></div>
                  <h3 className="text-2xl font-display font-bold text-[--text-primary]">
                    {t.about.solutions}
                  </h3>
                </div>
                <p className="text-base md:text-lg text-[--text-secondary] leading-relaxed">
                  {t.about.solutionsDesc}
                </p>
              </motion.div>
            </div>
          </motion.div>

          {/* Key Highlights */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6 }}
            className="mb-24"
          >
            <div className="relative bg-gradient-to-r from-[--accent]/10 via-yellow-400/10 to-[--accent]/10 rounded-3xl p-8 md:p-10 overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-transparent via-[--bg-primary]/50 to-transparent"></div>

              <div className="relative z-10">
                <div className="text-center mb-6">
                  <h3 className="text-2xl md:text-3xl font-display font-bold text-[--text-primary] mb-3">
                    {t.about.versatility}
                  </h3>
                  <div className="w-20 h-1 bg-gradient-to-r from-[--accent] to-yellow-400 rounded-full mx-auto"></div>
                </div>

                <p className="text-lg md:text-xl text-[--text-secondary] leading-relaxed text-center max-w-4xl mx-auto">
                  {t.about.versatilityDesc}
                </p>
              </div>
            </div>
          </motion.div>

          {/* I NOSTRI VALORI - Expandable Cards */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6 }}
            className="mb-24"
          >
            <h3 className="text-3xl md:text-4xl font-display font-bold text-[--text-primary] mb-12 text-center">
              {t.about.ourValues.split(' ')[0]} <span className="gradient-text">{t.about.ourValues.split(' ').slice(1).join(' ')}</span>
            </h3>
            <div className="grid md:grid-cols-2 gap-6">
              {valuesData.map((value, index) => {
                const IconComponent = value.icon;
                const isExpanded = expandedValue === value.id;

                return (
                  <motion.div
                    key={value.id}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    onClick={() => setExpandedValue(isExpanded ? null : value.id)}
                    className="cursor-pointer"
                  >
                    <motion.div
                      layout
                      className="glass rounded-3xl p-8 hover:shadow-luxury-hover transition-all duration-300 border border-transparent hover:border-[--accent]/30"
                    >
                      <motion.div layout="position" className="flex items-start mb-4">
                        <div className="w-12 h-12 rounded-xl bg-[--accent]/20 flex items-center justify-center mr-4 flex-shrink-0">
                          <IconComponent className="w-6 h-6 text-[--accent]" />
                        </div>
                        <div className="flex-grow">
                          <h4 className="text-xl font-bold text-[--accent] mb-2">
                            {value.title}
                          </h4>
                          <p className="text-[--text-secondary] leading-relaxed text-sm">
                            {value.short}
                          </p>
                        </div>
                      </motion.div>

                      <AnimatePresence>
                        {isExpanded && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: "auto" }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.3 }}
                            className="overflow-hidden"
                          >
                            <div className="pt-4 border-t border-[--accent]/20 mt-4">
                              <p className="text-[--text-secondary] leading-relaxed">
                                {value.full}
                              </p>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>

                      <motion.div
                        layout="position"
                        className="mt-4 text-[--accent] text-sm font-medium flex items-center"
                      >
                        <span>{isExpanded ? t.about.showLess : t.about.showMore}</span>
                        <motion.span
                          animate={{ rotate: isExpanded ? 180 : 0 }}
                          transition={{ duration: 0.3 }}
                          className="ml-2"
                        >
                          ▼
                        </motion.span>
                      </motion.div>
                    </motion.div>
                  </motion.div>
                );
              })}
            </div>
            <p className="text-center text-[--text-secondary] italic mt-8">
              Ellegi team<br />
              <span className="text-[--accent] font-semibold">…Your Partner in light</span>
            </p>
          </motion.div>

          {/* LE NOSTRE COMPETENZE - Professional Animated Cards */}
          {counterPhase === "header" && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1 }}
              className="mb-24"
            >
              <h3 className="text-3xl md:text-4xl font-display font-bold text-[--text-primary] mb-12 text-center">
                {t.about.ourSkills.split(' ')[0]} {t.about.ourSkills.split(' ')[1]} <span className="gradient-text">{t.about.ourSkills.split(' ').slice(2).join(' ')}</span>
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {[
                  { title: t.about.skill1Title, desc: t.about.skill1Desc, icon: Target },
                  { title: t.about.skill2Title, desc: t.about.skill2Desc, icon: Zap },
                  { title: t.about.skill3Title, desc: t.about.skill3Desc, icon: Sparkles },
                  { title: t.about.skill4Title, desc: t.about.skill4Desc, icon: Shield }
                ].map((item, index) => {
                  const IconComponent = item.icon;

                  return (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 30 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true, margin: "-50px" }}
                      transition={{
                        delay: index * 0.1,
                        duration: 0.5,
                        type: "spring",
                        stiffness: 100
                      }}
                      whileHover={{
                        y: -8,
                        transition: { duration: 0.3 }
                      }}
                      className="group"
                    >
                      <div className="relative rounded-2xl p-8 h-full transition-all duration-300 overflow-hidden bg-[--bg-primary]/40 backdrop-blur-sm border border-[--accent]/10 hover:border-[--accent]/30 hover:shadow-luxury-hover">
                        {/* Subtle gradient overlay on hover */}
                        <div className="absolute inset-0 bg-gradient-to-br from-[--accent]/5 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                        {/* Icon */}
                        <div className="relative w-14 h-14 rounded-xl bg-[--accent]/10 flex items-center justify-center mb-6 mx-auto group-hover:bg-[--accent]/20 transition-colors duration-300">
                          <IconComponent className="w-7 h-7 text-[--accent]" />
                        </div>

                        {/* Content */}
                        <div className="relative z-10 text-center">
                          <h4 className="text-xl font-bold text-[--accent] mb-4 group-hover:scale-105 transition-transform duration-300">
                            {item.title}
                          </h4>

                          <p className="text-[--text-secondary] text-sm leading-relaxed group-hover:text-[--text-primary] transition-colors duration-300">
                            {item.desc}
                          </p>
                        </div>

                        {/* Bottom accent line */}
                        <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-[--accent] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </motion.div>
          )}

          {/* LE NOSTRE SOLUZIONI PER */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6 }}
            className="mb-24"
          >
            <h3 className="text-3xl md:text-4xl font-display font-bold text-[--text-primary] mb-12 text-center">
              {t.about.ourSolutions.split(' ')[0]} {t.about.ourSolutions.split(' ')[1]} <span className="gradient-text">{t.about.ourSolutions.split(' ').slice(2).join(' ')}</span>
            </h3>

            {/* Spazi Interni */}
            <div className="mb-12">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="flex items-center justify-center mb-8"
              >
                <div className="h-px w-12 bg-gradient-to-r from-transparent to-[--accent]"></div>
                <h4 className="text-2xl font-bold text-[--accent] mx-4">{t.about.indoorSpaces}</h4>
                <div className="h-px w-12 bg-gradient-to-l from-transparent to-[--accent]"></div>
              </motion.div>

              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                {[
                  t.about.indoor1, t.about.indoor2, t.about.indoor3,
                  t.about.indoor4, t.about.indoor5, t.about.indoor6,
                  t.about.indoor7, t.about.indoor8, t.about.indoor9
                ].map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.05, duration: 0.3 }}
                    whileHover={{ scale: 1.05, y: -4 }}
                    className="group"
                  >
                    <div className="relative bg-[--bg-primary]/40 backdrop-blur-sm rounded-xl p-4 text-center hover:bg-[--bg-primary]/60 transition-all duration-300 border border-[--accent]/10 hover:border-[--accent]/30 overflow-hidden">
                      <div className="absolute inset-0 bg-gradient-to-br from-[--accent]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                      <p className="relative z-10 text-[--text-secondary] text-sm font-medium group-hover:text-[--accent] transition-colors duration-300">
                        {item}
                      </p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Spazi Esterni */}
            <div>
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="flex items-center justify-center mb-8"
              >
                <div className="h-px w-12 bg-gradient-to-r from-transparent to-yellow-400"></div>
                <h4 className="text-2xl font-bold text-yellow-400 mx-4">{t.about.outdoorSpaces}</h4>
                <div className="h-px w-12 bg-gradient-to-l from-transparent to-yellow-400"></div>
              </motion.div>

              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {[
                  t.about.outdoor1, t.about.outdoor2, t.about.outdoor3,
                  t.about.outdoor4, t.about.outdoor5, t.about.outdoor6,
                  t.about.outdoor7
                ].map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.05, duration: 0.3 }}
                    whileHover={{ scale: 1.05, y: -4 }}
                    className="group"
                  >
                    <div className="relative bg-[--bg-primary]/40 backdrop-blur-sm rounded-xl p-4 text-center hover:bg-[--bg-primary]/60 transition-all duration-300 border border-yellow-400/10 hover:border-yellow-400/30 overflow-hidden">
                      <div className="absolute inset-0 bg-gradient-to-br from-yellow-400/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                      <p className="relative z-10 text-[--text-secondary] text-sm font-medium group-hover:text-yellow-400 transition-colors duration-300">
                        {item}
                      </p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Final Inspirational Section */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6 }}
            className="mb-20"
          >
            <div className="relative bg-gradient-to-br from-[--bg-primary]/60 to-[--bg-primary]/30 backdrop-blur-sm rounded-3xl p-10 md:p-16 overflow-hidden">
              {/* Decorative elements */}
              <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-bl from-[--accent]/10 to-transparent rounded-full blur-3xl"></div>
              <div className="absolute bottom-0 left-0 w-72 h-72 bg-gradient-to-tr from-yellow-400/10 to-transparent rounded-full blur-3xl"></div>

              <div className="relative z-10">
                {/* Main heading */}
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6 }}
                  className="text-center mb-12"
                >
                  <h3 className="text-3xl md:text-5xl font-display font-bold text-[--text-primary] mb-6 leading-tight">
                    {t.about.lightingFuture.split(' ')[0]} {t.about.lightingFuture.split(' ')[1]} <span className="gradient-text">{t.about.lightingFuture.split(' ').slice(2).join(' ')}</span>
                  </h3>
                  <div className="w-24 h-1 bg-gradient-to-r from-[--accent] via-yellow-400 to-[--accent] rounded-full mx-auto mb-6"></div>
                  <p className="text-lg md:text-xl text-[--text-secondary] max-w-3xl mx-auto leading-relaxed">
                    {t.about.lightingFutureDesc}
                  </p>
                </motion.div>

                {/* Stats Grid */}
                <div className="grid md:grid-cols-3 gap-8 mb-12">
                  {[
                    { number: "40+", label: t.about.yearsExperience, icon: "🏆" },
                    { number: "10000+", label: t.about.projectsCompleted, icon: "💡" },
                    { number: "100%", label: t.about.passionDedication, icon: "❤️" }
                  ].map((stat, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, scale: 0.9 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1, duration: 0.5 }}
                      className="text-center"
                    >
                      <div className="text-5xl mb-3">{stat.icon}</div>
                      <div className="text-4xl md:text-5xl font-bold gradient-text mb-2">{stat.number}</div>
                      <div className="text-[--text-secondary] font-medium">{stat.label}</div>
                    </motion.div>
                  ))}
                </div>

                {/* Quote */}
                <motion.div
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.3, duration: 0.6 }}
                  className="text-center border-t border-[--accent]/20 pt-8"
                >
                  <p className="text-xl md:text-2xl text-[--text-primary] italic mb-4 leading-relaxed">
                    "{t.about.lightQuote}"
                  </p>
                  <p className="text-[--text-secondary] font-medium">
                    — Il Team <span className="text-[--accent] font-bold" style={{ fontFamily: "'Bauhaus 93', sans-serif" }}>ELLEGI</span>
                  </p>
                </motion.div>
              </div>
            </div>
          </motion.div>
        </motion.div>

        {/* Gradient Footer */}
        <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-[--bg-primary] via-[--bg-primary]/60 to-transparent pointer-events-none" />
      </section>

      <Footer
        onNavigate={onNavigate}
        onOpenPrivacyPolicy={onOpenPrivacyPolicy}
        onOpenCookiePolicy={onOpenCookiePolicy}
        onOpenTermsOfService={onOpenTermsOfService}
      />
    </>
  );
}
